// src/utils/draftIntel.js

const clean = (s) => (s || "").toString().trim();

function winLoseFromGame(g) {
  return { win: Number(g?.win || 0), lose: Number(g?.lose || 0) };
}

function addStats(store, hero, kind, winAdd = 0, loseAdd = 0) {
  const h = clean(hero);
  if (!h) return;
  if (!store[h]) store[h] = { pick: 0, ban: 0, win: 0, lose: 0 };
  store[h][kind] += 1;
  store[h].win += winAdd;
  store[h].lose += loseAdd;
}

function finalize(store) {
  const out = {};
  for (const [hero, s] of Object.entries(store)) {
    const totalWL = (s.win || 0) + (s.lose || 0);
    out[hero] = {
      ...s,
      winRate: totalWL > 0 ? (s.win / totalWL) * 100 : null,
    };
  }
  return out;
}

/**
 * games: จาก parseGamesFromExcel
 * - g.fsPick: string[]
 * - g.fsBan:  string[]
 * - g.opPick: string[]
 * - g.opBan:  string[]
 * - g.competitor: string
 */
export function buildDraftIntel(games = []) {
  const global = {};
  const fs = {};
  const oppByTeam = {};

  for (const g of games) {
    const { win, lose } = winLoseFromGame(g);
    const opp = clean(g?.competitor) || "UNKNOWN";

    if (!oppByTeam[opp]) oppByTeam[opp] = {};

    (g.fsPick || []).forEach((h) => {
      addStats(fs, h, "pick", win, lose);
      addStats(global, h, "pick", win, lose);
    });
    (g.fsBan || []).forEach((h) => {
      addStats(fs, h, "ban", win, lose);
      addStats(global, h, "ban", win, lose);
    });

    (g.opPick || []).forEach((h) => {
      // Opponent win/lose should mirror FS lose/win in that game.
      addStats(oppByTeam[opp], h, "pick", lose, win);
      addStats(global, h, "pick", lose, win);
    });
    (g.opBan || []).forEach((h) => {
      addStats(oppByTeam[opp], h, "ban", 0, 0);
      addStats(global, h, "ban", 0, 0);
    });
  }

  const oppFinal = {};
  for (const [team, store] of Object.entries(oppByTeam)) oppFinal[team] = finalize(store);

  return {
    global: finalize(global),
    fs: finalize(fs),
    oppByTeam: oppFinal,
    teams: Object.keys(oppFinal).filter(Boolean).sort(),
  };
}

/**
 * MVP prediction: ใช้ tendency ของทีม + global fallback
 */
export function predictOpponentNextMove({
  intel,
  opponent,
  actionType, // "ban" | "pick"
  bannedNamesSet,
  pickedNamesSet,
  topK = 5,
  useGlobalFallback = true,
}) {
  const opp = intel?.oppByTeam?.[opponent] || {};
  const global = intel?.global || {};
  const smoothing = 0.25;

  const scoreMap = new Map();

  const consider = (hero, score) => {
    const h = clean(hero);
    if (!h) return;
    if (bannedNamesSet?.has(h) || pickedNamesSet?.has(h)) return;
    scoreMap.set(h, (scoreMap.get(h) || 0) + score);
  };

  // ทีมนี้นิยมอะไร
  for (const [hero, s] of Object.entries(opp)) {
    const banCount = Number(s?.ban || 0);
    const pickCount = Number(s?.pick || 0);
    const mainScore = actionType === "ban"
      ? (banCount > 0 ? banCount : pickCount)
      : pickCount;
    consider(hero, mainScore);
  }
  // fallback global
  if (useGlobalFallback) {
    for (const [hero, s] of Object.entries(global)) {
      consider(hero, smoothing * (actionType === "ban" ? (s.ban || 0) : (s.pick || 0)));
    }
  }

  const arr = [...scoreMap.entries()]
    .map(([hero, score]) => ({ hero, score }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, topK);

  const sum = arr.reduce((acc, x) => acc + x.score, 0) || 1;

  return arr.map((x) => ({
    hero: x.hero,
    pct: Math.round((x.score / sum) * 100),
    reason: [
      useGlobalFallback
        ? `อิง Opponent Pool ของ ${opponent} + global model`
        : `อิง Opponent Pool ของ ${opponent}`,
      actionType === "ban" ? "โหมดแนะนำแบน (ถ้าไม่มีสถิติแบนจะอิง pick tendency)" : "โหมดแนะนำ pick",
    ],
  }));
}

/**
 * แนะนำฝั่งเรา (hero เป็นชื่อ)
 */
export function scoreHeroForUs({ hero, intel, opponent }) {
  const h = clean(hero);
  if (!h) return null;

  const heroStats = intel?.heroStats?.[h];
  const g = intel?.global?.[h];
  const fs = intel?.fs?.[h];
  const opp = opponent ? intel?.oppByTeam?.[opponent]?.[h] : null;

  const metaPick = heroStats?.pick ?? g?.pick ?? 0;
  const metaBan = heroStats?.ban ?? g?.ban ?? 0;
  const metaWR = heroStats?.winRate ?? g?.winRate ?? 50;
  const fsWR = fs?.winRate ?? metaWR ?? 50;
  const fsGames = Number(fs?.pick ?? ((fs?.win || 0) + (fs?.lose || 0)) ?? 0);
  const fsConfidence = Math.min(1, fsGames / 20); // conservative confidence ramp

  // Keep scores in a tighter realistic band around 50.
  const metaVolume = metaPick + metaBan * 0.7;
  const metaComponent = Math.log1p(Math.max(0, metaVolume)) * 2.2 + (metaWR - 50) * 0.12;
  const formComponent = (fsWR - 50) * (0.45 + fsConfidence * 0.35);
  const opponentFocusComponent = ((opp?.ban || 0) + (opp?.pick || 0)) * 0.08;
  const rawScore = 50 + formComponent + metaComponent * 0.45 + opponentFocusComponent;
  const score = Math.max(40, Math.min(78, rawScore));

  return {
    hero: h,
    score,
    explain: [
      `Meta: ${metaPick} pick / ${metaBan} ban`,
      `FS Form: ${fsWR != null ? Number(fsWR).toFixed(1) + "%" : "n/a"} (${fsGames} เกม)`,
      opponent ? `Vs ${opponent}: pick+ban ${(opp?.pick || 0) + (opp?.ban || 0)} ครั้ง` : null,
    ].filter(Boolean),
  };
}
